/**
 * Maintenance Wrapper
 * 
 * Wraps the entire website and shows maintenance page if enabled
 * EXCLUDES: Admin routes (/manage/*) are never blocked
 */

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, RefreshCw, Clock } from 'lucide-react';

interface MaintenanceWrapperProps {
  children: React.ReactNode;
}

export default function MaintenanceWrapper({ children }: MaintenanceWrapperProps) {
  const location = useLocation();
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [isChecking, setIsChecking] = useState(true);

  // 🔥 BYPASS: Admin routes are NEVER blocked by maintenance mode
  const isAdminRoute = location.pathname.startsWith('/manage');

  useEffect(() => {
    // Skip check for admin routes
    if (isAdminRoute) {
      setIsChecking(false);
      setIsMaintenanceMode(false);
      return;
    }

    // Check ONCE on mount (maintenance is very rare)
    checkMaintenanceMode();
  }, [isAdminRoute]);

  const checkMaintenanceMode = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.metapic.app';
      const response = await axios.get(`${apiUrl}/api/v1/site-settings/public`);
      
      if (response.data.success && response.data.data) {
        const maintenanceWeb = response.data.data.maintenance_web;
        
        if (maintenanceWeb && typeof maintenanceWeb === 'object') {
          setIsMaintenanceMode(maintenanceWeb.enabled === true);
          setMaintenanceMessage(maintenanceWeb.message || 'We are currently performing scheduled maintenance. Please check back soon!');
        }
      }
    } catch (error) {
      console.error('Failed to check maintenance mode:', error);
      // On error, don't block access
      setIsMaintenanceMode(false);
    } finally {
      setIsChecking(false);
    }
  };

  // Show loading initially
  if (isChecking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show maintenance page
  if (isMaintenanceMode) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-pink-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          {/* Card */}
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-500 to-red-500 p-8 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-full mb-4">
                <AlertTriangle className="w-10 h-10 text-orange-500" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">
                Under Maintenance
              </h1>
              <p className="text-orange-100 text-lg">
                We'll be back soon!
              </p>
            </div>

            {/* Body */}
            <div className="p-8 space-y-6">
              {/* Message - Support HTML */}
              <div className="text-center">
                <div 
                  className="text-gray-700 text-lg leading-relaxed prose prose-lg max-w-none"
                  dangerouslySetInnerHTML={{ __html: maintenanceMessage }}
                />
              </div>

              {/* Divider */}
              <div className="border-t border-gray-200"></div>

              {/* Info */}
              <div className="flex items-center justify-center gap-3 text-gray-600">
                <Clock className="w-5 h-5" />
                <p className="text-sm">
                  We apologize for any inconvenience
                </p>
              </div>

              {/* Footer Note */}
              <p className="text-center text-sm text-gray-500">
                If you need immediate assistance, please contact our support team
              </p>
            </div>
          </div>

          {/* Logo/Brand (Optional) */}
          <div className="text-center mt-8">
            <p className="text-gray-600 text-sm">
              © 2025 MetaPic - AI-Powered Metadata Generator
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Normal mode - render children
  return <>{children}</>;
}
